import React from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { useInView, usePlayback, usePrefersReducedMotion } from '../pbi-development/coworkChatHooks';
import { QuerySubmitButton } from './DynamicVisualizationPanel';
import {
  SEMHR_CONVERSATION,
  SEMHR_CONVERSATION_DELAYS,
  SEMHR_CONVERSATION_LOOP_PAUSE,
  SEMHR_CONVERSATION_STEPS,
} from './semanticHrTourData';
import { HeadcountBuHorizChartSvg } from './semanticHrTourCharts';

function Step({ show, children }) {
  if (!show) return null;
  return <div className="ia-semhr-block">{children}</div>;
}

function SegmentedSwitch({ value, options, className = '' }) {
  return (
    <div className={`inline-flex items-center rounded-full border border-ink-200/90 bg-surface-subtle/60 p-0.5 ${className}`}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <span
            key={opt.id}
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold leading-none transition-colors ${
              active ? 'bg-white text-brand-700 shadow-sm' : 'text-ink-500'
            }`}
          >
            {opt.icon ? <span className="inline-flex shrink-0 items-center justify-center">{opt.icon}</span> : null}
            {opt.label}
          </span>
        );
      })}
    </div>
  );
}

function Composer({ isLoading = false }) {
  return (
    <div className="ia-semhr-conversation-composer shrink-0 border-t border-ink-100 bg-white px-2 py-1">
      <div className="rounded-lg border border-ink-200/90 px-1.5 py-1">
        <p className="text-[7px] text-ink-400">Ask about headcount, attrition, hiring, workforce planning…</p>
        <div className="mt-0.5 flex items-center justify-between gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-0.5">
            <span className="rounded-full border border-ink-200/90 px-1 py-px text-[7px] text-ink-600">Ask</span>
            <span className="truncate rounded-full border border-ink-200/90 px-1 py-px text-[7px] text-ink-600">SemanticHR Copilot Analyst</span>
            <span className="rounded-full border border-ink-200/90 px-1 py-px text-[7px] text-ink-600">Mistral Large</span>
          </div>
          <QuerySubmitButton state={isLoading ? 'loading' : 'ready'} size="sm" />
        </div>
      </div>
      <p className="mt-0.5 text-center text-[7px] leading-tight text-ink-400">Copilot uses Snowflake Cortex. Results are governed by your HR data model.</p>
    </div>
  );
}

function ChartSqlToggles() {
  return (
    <div className="flex justify-end">
      <SegmentedSwitch
        value="chart"
        options={[
          { id: 'chart', label: 'Chart', icon: <ChartBarIcon className="h-2 w-2" aria-hidden="true" /> },
          { id: 'sql', label: 'SQL', icon: <CodeBracketIcon className="h-2 w-2" aria-hidden="true" /> },
        ]}
        className="text-[7px]"
      />
    </div>
  );
}

function HeadcountChart() {
  const { chartTitle } = SEMHR_CONVERSATION;
  return (
    <div className="ia-semhr-conversation-chart overflow-hidden rounded-md border border-ink-200/90 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-1.5 py-0.5">
        <p className="text-[7px] font-semibold text-ink-800">{chartTitle}</p>
        <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[7px] font-medium text-ink-600">
          Explore <ArrowTopRightOnSquareIcon className="h-1.5 w-1.5" />
        </span>
      </div>
      <div className="px-1 py-0.5">
        <HeadcountBuHorizChartSvg compact />
      </div>
    </div>
  );
}

function QueryResultsTable() {
  const { tableTitle, columns, rows, footer } = SEMHR_CONVERSATION;
  return (
    <div className="ia-semhr-conversation-table overflow-hidden rounded-md border border-ink-200/80 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-1.5 py-0.5">
        <p className="text-[7px] font-semibold text-ink-800">{tableTitle}</p>
        <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[7px] font-medium text-ink-600">
          Explore <ArrowTopRightOnSquareIcon className="h-1.5 w-1.5" />
        </span>
      </div>
      <table className="w-full text-[7px]">
        <thead>
          <tr className="border-b border-ink-100 bg-ink-50/60 text-[7px] font-semibold uppercase tracking-wide text-ink-500">
            {columns.map((col) => (
              <th key={col} className={`px-1 py-0.5 ${col !== columns[0] ? 'text-right' : 'text-left'}`}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} className="border-b border-ink-50 text-ink-700">
              {row.map((cell, idx) => (
                <td key={cell} className={`px-1 py-px ${idx !== 0 ? 'text-right tabular-nums' : ''}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="py-0.5 text-center text-[7px] font-medium text-brand-600">{footer}</p>
    </div>
  );
}

export default function SemanticHRConversationVisual({ className = '' }) {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView(0.12);
  const { isVisible } = usePlayback(
    inView,
    {
      steps: SEMHR_CONVERSATION_STEPS,
      stepDelays: SEMHR_CONVERSATION_DELAYS,
      loopPause: SEMHR_CONVERSATION_LOOP_PAUSE,
    },
    reducedMotion,
  );

  const showAnalyzing = isVisible('analyzing') && !isVisible('intro');
  const copy = SEMHR_CONVERSATION;

  return (
    <div
      ref={ref}
      className={`ia-semhr-conversation ia-semhr-tour ia-semhr-video-frame pointer-events-none select-none overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm ${className}`.trim()}
      role="img"
      aria-label="Mock-up of a Semantic HR copilot conversation: a headcount question answered with a chart and a query results table"
    >
      <div aria-hidden="true" className="contents">
      <div className="ia-semhr-tour-body">
        <div className="ia-semhr-tour-main bg-white">
          <div className="ia-semhr-conversation-inner flex min-h-0 w-full flex-1 flex-col">
            <div className="ia-semhr-conversation-chat min-h-0 flex-1 overflow-hidden">
              <div className="w-full px-2 py-1">
                <div className="space-y-1">
                  <Step show={isVisible('question')}>
                    <div className="flex justify-end">
                      <div className="max-w-[88%] rounded-lg rounded-tr-sm bg-ink-100/80 px-1.5 py-1 text-[8px] leading-snug text-ink-800">
                        {copy.question}
                      </div>
                    </div>
                  </Step>
                  {showAnalyzing ? (
                    <div className="ia-semhr-block flex items-center gap-1 text-[7px] text-ink-500">
                      <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-brand-200 border-t-brand-500" />
                      Analyzing your question…
                    </div>
                  ) : null}
                  <Step show={isVisible('intro')}>
                    <div className="border-l-2 border-brand-400 pl-1.5">
                      <p className="text-[7px] font-semibold uppercase tracking-wide text-brand-600">Analyst</p>
                      <p className="mt-px text-[7px] leading-snug text-ink-700">{copy.analyst}</p>
                    </div>
                  </Step>
                  <Step show={isVisible('chart') || isVisible('table')}>
                    <ChartSqlToggles />
                  </Step>
                  <Step show={isVisible('chart')}>
                    <HeadcountChart />
                  </Step>
                  <Step show={isVisible('table')}>
                    <QueryResultsTable />
                  </Step>
                </div>
              </div>
            </div>
            <Composer isLoading={showAnalyzing} />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
